/**
 * One-line paging control for the billing tables.
 *
 *   1–3 of 119 entries   Rows [3 v]   [<] [>]
 *
 * Replaces three stacked lines ("Showing…", "Items per page", and a row of
 * large Next / Previous buttons) that pushed Notes and Message History far
 * down the staff membership page.
 */
import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../ui";
import { useTheme } from "../../../context/ThemeContext";
import { getSecondaryText, getInputStyles } from "../../../utils/themeUtils";

interface BillingPagerProps {
  /** Total rows in the table. */
  total: number;
  /** 1-based page being shown. */
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Omit to hide the "Rows" selector. */
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  /** Plural noun for the rows, e.g. "entries" or "invoices". */
  itemLabel?: string;
  /** Singular noun for the rows. */
  itemLabelSingular?: string;
  /** Accessible name of the button that goes to page - 1. */
  previousLabel: string;
  /** Accessible name of the button that goes to page + 1. */
  nextLabel: string;
  /** Unique prefix for the selector's id, so two pagers can share a page. */
  idPrefix: string;
  className?: string;
}

const BillingPager: React.FC<BillingPagerProps> = ({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [],
  itemLabel = "entries",
  itemLabelSingular = "entry",
  previousLabel,
  nextLabel,
  idPrefix,
  className = "",
}) => {
  const { isDarkMode } = useTheme();
  if (!total || total <= 0) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  const hasPages = total > pageSize;
  const smallestOption = pageSizeOptions.length > 0 ? Math.min(...pageSizeOptions) : pageSize;
  // A selector that cannot change what is on screen reads as broken.
  const showSizeSelector = Boolean(onPageSizeChange) && pageSizeOptions.length > 1 && total > smallestOption;
  const selectId = `${idPrefix}-rows-per-page`;

  return (
    <div
      data-testid={`${idPrefix}-pager`}
      className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm ${getSecondaryText(
        isDarkMode
      )} ${className}`}
    >
      <span aria-live="polite">
        {hasPages
          ? `${first}–${last} of ${total} ${itemLabel}`
          : `All ${total} ${total === 1 ? itemLabelSingular : itemLabel} shown`}
      </span>

      <div className="flex items-center gap-3">
        {showSizeSelector && (
          <div className="flex items-center gap-1.5">
            <label htmlFor={selectId}>Rows</label>
            <select
              id={selectId}
              value={pageSize}
              onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
              className={`rounded px-2 py-1 text-sm ${getInputStyles(isDarkMode)}`}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}

        {hasPages && (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              iconOnly
              aria-label={previousLabel}
              title={previousLabel}
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              className="min-h-[36px] min-w-[36px] justify-center"
              icon={<ChevronLeft className="w-4 h-4" aria-hidden="true" />}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              iconOnly
              aria-label={nextLabel}
              title={nextLabel}
              disabled={last >= total}
              onClick={() => onPageChange(page + 1)}
              className="min-h-[36px] min-w-[36px] justify-center"
              icon={<ChevronRight className="w-4 h-4" aria-hidden="true" />}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPager;
